import { useFrame, useThree } from '@react-three/fiber'
import { useEffect } from 'react'
import type { GameRuntime } from '../game/runtime'
import { Car } from './Car'
import { type CameraMode, FollowCamera } from './FollowCamera'
import { NightEnvironment } from './NightEnvironment'
import { OffscreenRacerIndicators } from './OffscreenRacerIndicators'
import { RaceEffects } from './RaceEffects'
import { SceneWarmup } from './SceneWarmup'
import { getSceneCarDescriptors } from './scene-contract'
import { Track } from './Track'

interface RaceSceneProps {
  runtime: GameRuntime
  cameraMode: CameraMode
  reducedMotion: boolean
  manualSimulation: boolean
  onPrepared: () => void
  onPreparationError: (error: Error) => void
}

export function RaceScene({
  runtime,
  cameraMode,
  reducedMotion,
  manualSimulation,
  onPrepared,
  onPreparationError,
}: RaceSceneProps) {
  const initial = runtime.getState()
  const cars = getSceneCarDescriptors(initial)
  const invalidate = useThree((state) => state.invalidate)
  useEffect(() => {
    if (!manualSimulation) return
    return runtime.subscribe(invalidate)
  }, [invalidate, manualSimulation, runtime])
  useFrame((_, delta) => {
    if (!manualSimulation) runtime.stepFrame(delta)
  }, -100)

  return (
    <>
      <NightEnvironment />
      <Track track={initial.track} runtime={runtime} />
      {cars.map((car) => (
        <Car key={car.vehicleId} runtime={runtime} vehicleId={car.vehicleId} index={car.index} />
      ))}
      <FollowCamera runtime={runtime} mode={cameraMode} reducedMotion={reducedMotion} />
      <OffscreenRacerIndicators runtime={runtime} />
      <RaceEffects runtime={runtime} reducedMotion={reducedMotion} />
      <SceneWarmup onReady={onPrepared} onError={onPreparationError} />
    </>
  )
}
